import { Component, booleanAttribute, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageService } from '../page.service';

@Component({
  selector: 'glxy-page-nav-button',
  templateUrl: './page-nav-button.component.html',
  styleUrls: ['./page-nav-button.component.scss'],
  imports: [MatIconModule, MatButtonModule, MatTooltipModule],
})
export class PageNavButtonComponent {
  private readonly pageService = inject(PageService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute, { optional: true });

  /** The URL/path to navigate to when the button is clicked */
  readonly previousPageUrl = input<string | undefined>();
  /** The tooltip text to display on hover */
  readonly previousPageTitle = input<string | undefined>();
  /** Whether to use the page history service to determine the previous URL */
  readonly useHistory = input(false, { transform: booleanAttribute });
  /** The title to use when history provides a different URL than the default */
  readonly historyBackButtonTitle = input('Back');
  /** Whether to treat the previousPageUrl as relative to the current route */
  readonly useRelativePath = input(false, { transform: booleanAttribute });

  protected readonly currentPreviousPageUrl = linkedSignal<string | undefined>(() => this.previousPageUrl());
  protected readonly currentPreviousPageTitle = linkedSignal<string | undefined>(() => this.previousPageTitle());
  private readonly defaultPageUrl = signal('');

  constructor() {
    // Initialize defaultPageUrl when previousPageUrl changes
    effect(() => {
      const previousUrl = this.previousPageUrl();
      if (previousUrl) {
        this.defaultPageUrl.set(previousUrl);
      }
    });

    // Health check: if the ActivedRoute is not available when using a relative path, log an error
    effect(() => {
      if (this.useRelativePath() && !this.activatedRoute) {
        console.error('ActivatedRoute is not available when using a relative path');
      }
    });
  }

  navigateToPreviousUrl(): void {
    if (this.useHistory()) {
      const previousUrl = this.pageService.getPreviousPageUrl(this.defaultPageUrl());
      this.currentPreviousPageUrl.set(previousUrl);

      // if the previous page url is different than what was initially provided
      // then use the back button title text if provided
      if (previousUrl !== this.defaultPageUrl() && this.historyBackButtonTitle()) {
        this.currentPreviousPageTitle.set(this.historyBackButtonTitle());
      }
    }

    // pop will remove the last two items from the navigation history.
    // the page that was just visited and the page we are about to navigate back to
    // this prevents history looping
    this.pageService.pop();

    // Early return if router is not available when using a relative path
    if (this.useRelativePath() && !this.activatedRoute) {
      return;
    }

    // Redirect with explicit query param and fragment assignment. If there are query params, remove it from the path
    const urlToNavigate = this.currentPreviousPageUrl() ?? '';
    const urlTree = this.router.parseUrl(urlToNavigate);
    const queryParamIndex = urlToNavigate.indexOf('?');
    const path = queryParamIndex > 0 ? urlToNavigate.substring(0, queryParamIndex) : urlToNavigate;

    if (path) {
      const navigationExtras = {
        queryParams: urlTree.queryParams,
        fragment: urlTree.fragment ?? undefined,
        ...(this.useRelativePath() && { relativeTo: this.activatedRoute }),
      };

      this.router.navigate([path], navigationExtras);
    }
  }
}
